import type { PropsWithChildren } from 'react'
import React, { forwardRef } from 'react'
import { useFormikContext } from 'formik'
import { FormattedMessage } from 'react-intl'

import { TtButton, TtFieldset } from '@23/react'
import type { JSX } from '@23/web-components'

import type { StyleReactProps } from './index'

interface ExtraProps {
  onCancel?: () => void
}

// eslint-disable-next-line react/display-name
const TtSubmitButtonFormik = forwardRef<
  HTMLTtButtonElement,
  PropsWithChildren<
    JSX.TtButton &
      Omit<React.HTMLAttributes<HTMLTtButtonElement>, keyof StyleReactProps> &
      StyleReactProps
  > &
    ExtraProps
>(
  (
    {
      children,
      loading,
      disabled,
      label,
      className,
      style,
      onCancel,
      ...props
    },
    ref
  ) => {
    const formikContext = useFormikContext<{ default: never }>()
    const defaultError = formikContext.errors.default as string | undefined

    return (
      <TtFieldset
        label={label}
        direction="horizontal"
        reverse
        hideLabel
        errorText={defaultError}
        invalid={!!defaultError}
        className={className}
        style={style}
      >
        <TtButton
          ref={ref}
          {...props}
          loading={loading ?? formikContext.isSubmitting}
          disabled={
            disabled ??
            (!formikContext.dirty && !(loading ?? formikContext.isSubmitting))
          }
          submit
        >
          {children ?? <FormattedMessage defaultMessage="Submit" />}
        </TtButton>
        {onCancel && (
          <TtButton
            {...props}
            color="secondary"
            variant="light"
            onClick={onCancel}
          >
            <FormattedMessage defaultMessage="Cancel" />
          </TtButton>
        )}
      </TtFieldset>
    )
  }
)

export { TtSubmitButtonFormik }
